// src/features/etichette/StoricoEtichette.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Calendar,
  Eye,
  FileDown,
  Link as LinkIcon,
  Copy,
  QrCode,
  Search,
  Trash2,
  User,
  X,
} from "lucide-react";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function toYmd(d) {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
}

function startOfMonthYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-01`;
}

function formatDateTime(str) {
  if (!str) return "";
  return str.replace("T", " ").substring(0, 16);
}

function groupKeyFromDataProduzione(data_produzione, mode) {
  if (!data_produzione) return "Senza data";
  const s = data_produzione.replace("T", " ");
  const ymd = s.substring(0, 10); // YYYY-MM-DD
  if (mode === "day") return ymd;
  if (mode === "month") return ymd.substring(0, 7); // YYYY-MM
  return ""; // none
}

function prettyGroupLabel(key, mode) {
  if (!key) return "";
  if (mode === "day") return `Giorno ${key}`;
  if (mode === "month") return `Mese ${key}`;
  return key;
}

function getExpiry(label) {
  if (label?.scadenza) return String(label.scadenza).substring(0, 10);
  const match = String(label?.note || "").match(
    /\[\[SCADENZA_UI:(\d{4}-\d{2}-\d{2})\]\]/
  );
  return match?.[1] || "";
}

export default function StoricoEtichette({ readOnly = false }) {
  const [etichette, setEtichette] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [groupBy, setGroupBy] = useState("none"); // none|day|month

  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [duplicate, setDuplicate] = useState(null);
  const [duplicateDates, setDuplicateDates] = useState({
    data_produzione: "",
    scadenza: "",
  });

  const buildQuery = (overrides = {}) => {
    const params = new URLSearchParams();
    params.set("limit", "200");

    const s = (overrides.search ?? search).trim();
    const from = overrides.dateFrom ?? dateFrom;
    const to = overrides.dateTo ?? dateTo;
    if (s) params.set("search", s);
    if (from) params.set("date_from", from);
    if (to) params.set("date_to", to);

    return params.toString();
  };

  const loadData = (overrides = {}) => {
    setLoading(true);
    setError("");

    fetch(`/api/etichette_list.php?${buildQuery(overrides)}`, { credentials: "include" })
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data?.error || "Errore nel caricamento delle etichette");
        return data;
      })
      .then((data) => {
        if (Array.isArray(data)) setEtichette(data);
        else setError(data?.error || "Errore nel caricamento delle etichette");
      })
      .catch((err) => setError(err?.message || "Errore di rete"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getVisualizzaDdtUrl = (etichetta) => {
    const token = String(etichetta?.public_token || "").trim();
    if (token) {
      return `/api/visualizza_ddt.php?t=${encodeURIComponent(token)}`;
    }

    if (!etichetta?.ddt_ids) return null;
    const cleaned = String(etichetta.ddt_ids)
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean)
      .join(",");
    if (!cleaned) return null;
    return `/api/visualizza_ddt.php?ids=${encodeURIComponent(cleaned)}`;
  };

  const ddtCount = (ddt_ids) => {
    if (!ddt_ids) return 0;
    return String(ddt_ids)
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean).length;
  };

  // Raggruppamento
  const grouped = useMemo(() => {
    if (groupBy === "none") return [{ key: "", items: etichette }];

    const map = new Map();
    for (const e of etichette) {
      const key = groupKeyFromDataProduzione(e.data_produzione, groupBy);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    }
    const keys = Array.from(map.keys()).sort((a, b) => (a < b ? 1 : -1));
    return keys.map((k) => ({ key: k, items: map.get(k) }));
  }, [etichette, groupBy]);

  const setPresetOggi = () => {
    const today = toYmd(new Date());
    setDateFrom(today);
    setDateTo(today);
    loadData({ dateFrom: today, dateTo: today });
  };

  const setPresetUltimi7 = () => {
    const d = new Date();
    const to = toYmd(d);
    d.setDate(d.getDate() - 6);
    const from = toYmd(d);
    setDateFrom(from);
    setDateTo(to);
    loadData({ dateFrom: from, dateTo: to });
  };

  const setPresetQuestoMese = () => {
    const from = startOfMonthYmd();
    const to = toYmd(new Date());
    setDateFrom(from);
    setDateTo(to);
    loadData({ dateFrom: from, dateTo: to });
  };

  const clearDates = () => {
    setDateFrom("");
    setDateTo("");
    loadData({ dateFrom: "", dateTo: "" });
  };

  const manageLabel = async (payload) => {
    const response = await fetch("/api/etichette_manage.php", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.ok) {
      throw new Error(data?.error || "Operazione non riuscita");
    }
    return data;
  };

  const deleteLabel = async (label) => {
    if (!window.confirm(`Eliminare definitivamente l'etichetta “${label.preparazione || label.lotto}”?`)) return;
    setBusyId(label.id);
    setError("");
    try {
      await manageLabel({ action: "delete", id: label.id });
      setEtichette((items) => items.filter((item) => item.id !== label.id));
    } catch (err) {
      setError(err?.message || "Impossibile eliminare l'etichetta");
    } finally {
      setBusyId(null);
    }
  };

  const openDuplicate = (label) => {
    const source = String(label.data_produzione || "").replace(" ", "T").substring(0, 16);
    setDuplicateDates({ data_produzione: source, scadenza: getExpiry(label) });
    setDuplicate(label);
    setError("");
  };

  const submitDuplicate = async (event) => {
    event.preventDefault();
    if (!duplicate) return;
    setBusyId(duplicate.id);
    setError("");
    try {
      await manageLabel({ action: "duplicate", id: duplicate.id, ...duplicateDates });
      setDuplicate(null);
      loadData();
    } catch (err) {
      setError(err?.message || "Impossibile duplicare l'etichetta");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      {/* Toolbar filtri */}
      <div className="bg-white p-4 rounded-[24px] shadow-sm border border-gray-100 mb-6 flex flex-col md:flex-row items-center justify-between gap-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            loadData();
          }}
          className="relative w-full md:w-96"
        >
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca lotto o preparazione..."
            className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-[20px] text-sm focus:outline-none focus:ring-2 focus:ring-[#003B71] transition-all"
          />
        </form>

        <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto pb-1 md:pb-0">
          <button
            type="button"
            onClick={() => {
              setDateFrom("");
              setDateTo("");
              loadData({ dateFrom: "", dateTo: "" });
            }}
            className="px-4 py-2 rounded-full bg-[#003B71] text-white text-sm font-bold shadow-md whitespace-nowrap"
          >
            Tutti
          </button>

          <button
            type="button"
            onClick={setPresetOggi}
            className="px-4 py-2 rounded-full bg-white border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 whitespace-nowrap"
          >
            Oggi
          </button>

          <button
            type="button"
            onClick={setPresetUltimi7}
            className="px-4 py-2 rounded-full bg-white border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 whitespace-nowrap"
          >
            7 Giorni
          </button>

          <button
            type="button"
            onClick={setPresetQuestoMese}
            className="px-4 py-2 rounded-full bg-white border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 whitespace-nowrap"
          >
            Questo mese
          </button>

          <div className="h-6 w-px bg-gray-200 mx-1" />

          <div className="flex items-center gap-2 whitespace-nowrap">
            <Calendar className="w-4 h-4 text-gray-400" />
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-2 rounded-[14px] bg-gray-50 border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-[#003B71]"
              title="Da"
            />
            <span className="text-gray-400 text-sm">→</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-2 rounded-[14px] bg-gray-50 border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-[#003B71]"
              title="A"
            />

            <button
              type="button"
              onClick={loadData}
              className="px-4 py-2 rounded-[14px] bg-[#EEF2FF] text-[#003B71] text-sm font-bold hover:bg-[#003B71] hover:text-white transition-all"
              title="Applica date"
            >
              Applica
            </button>

            <button
              type="button"
              onClick={clearDates}
              className="px-4 py-2 rounded-[14px] bg-white border border-gray-200 text-gray-600 text-sm font-bold hover:border-gray-400 transition-all"
              title="Reset date"
            >
              Reset
            </button>

            <div className="h-6 w-px bg-gray-200 mx-1" />

            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              className="px-3 py-2 rounded-[14px] bg-gray-50 border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-[#003B71]"
              title="Raggruppa"
            >
              <option value="none">Nessun gruppo</option>
              <option value="day">Per giorno</option>
              <option value="month">Per mese</option>
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-[18px] border border-red-100 bg-red-50/60 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-gray-500">Caricamento etichette...</div>
      ) : etichette.length === 0 ? (
        <div className="text-sm text-gray-500">
          Nessuna etichetta trovata. Generane una dalla sezione “Gestione”.
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map((g) => (
            <div key={g.key || "all"} className="space-y-4">
              {groupBy !== "none" && (
                <div className="flex items-center justify-between px-1">
                  <div className="text-sm font-bold text-gray-700">
                    {prettyGroupLabel(g.key, groupBy)}
                  </div>
                  <div className="text-xs text-gray-400">
                    {g.items.length} etichette
                  </div>
                </div>
              )}

              {g.items.map((e) => {
                const visualizzaUrl = getVisualizzaDdtUrl(e);
                const count = ddtCount(e.ddt_ids);
                const statusDot =
                  count > 0 ? "bg-green-500" : "bg-gray-300";

                return (
                  <div
                    key={e.id}
                    className="bg-white rounded-[20px] p-5 shadow-sm border border-gray-100 hover:shadow-md transition-all flex flex-col md:flex-row gap-4 md:items-center justify-between group"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="text-[10px] font-mono bg-gray-100 text-gray-500 px-2 py-0.5 rounded-md">
                          Lotto: {e.lotto || `#${e.id}`}
                        </span>
                        <span className={`w-1.5 h-1.5 rounded-full ${statusDot}`} />
                      </div>

                      <h3 className="text-xl font-bold text-[#003B71] truncate mb-2">
                        {e.preparazione || "—"}
                      </h3>

                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          {formatDateTime(e.data_produzione)}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4 text-gray-400" />
                          {e.operatore || "—"}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 border-t md:border-t-0 md:border-l border-gray-100 pt-4 md:pt-0 md:pl-6">
                      <div className="flex items-center gap-2 bg-[#F6F8FA] px-3 py-2 rounded-xl text-xs font-medium text-gray-600 cursor-pointer hover:bg-blue-50 hover:text-[#003B71] transition-colors">
                        <LinkIcon className="w-3 h-3" />
                        {count} DDT collegati
                      </div>

                      <div className="flex items-center gap-2">
                        <a
                          href={`/api/etichette_pdf.php?id=${e.id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="w-10 h-10 rounded-[12px] bg-[#E0F7F2] text-[#008F70] flex items-center justify-center hover:bg-[#008F70] hover:text-white transition-all shadow-sm"
                          title="Scarica PDF"
                        >
                          <FileDown className="w-5 h-5" />
                        </a>

                        {visualizzaUrl ? (
                          <a
                            href={visualizzaUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="w-10 h-10 rounded-[12px] bg-[#EEF2FF] text-[#003B71] flex items-center justify-center hover:bg-[#003B71] hover:text-white transition-all shadow-sm"
                            title="Vedi documenti e foto etichetta"
                          >
                            <Eye className="w-5 h-5" />
                          </a>
                        ) : (
                          <button
                            type="button"
                            disabled
                            className="w-10 h-10 rounded-[12px] bg-[#EEF2FF] text-[#003B71]/40 flex items-center justify-center cursor-not-allowed"
                            title="Nessun documento o foto collegata"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                        )}

                        {e.qr_path ? (
                          <a
                            href={`/${e.qr_path}`}
                            target="_blank"
                            rel="noreferrer"
                            className="w-10 h-10 rounded-[12px] bg-white border border-gray-200 text-gray-400 flex items-center justify-center hover:text-gray-800 hover:border-gray-400 transition-all"
                            title="Apri QR"
                          >
                            <QrCode className="w-5 h-5" />
                          </a>
                        ) : (
                          <button
                            type="button"
                            disabled
                            className="w-10 h-10 rounded-[12px] bg-white border border-gray-200 text-gray-300 flex items-center justify-center cursor-not-allowed"
                            title="QR non disponibile"
                          >
                            <QrCode className="w-5 h-5" />
                          </button>
                        )}

                        {!readOnly && <button
                          type="button"
                          onClick={() => openDuplicate(e)}
                          disabled={busyId === e.id}
                          className="w-10 h-10 rounded-[12px] bg-amber-50 text-amber-700 flex items-center justify-center hover:bg-amber-600 hover:text-white transition-all disabled:opacity-50"
                          title="Duplica etichetta"
                        >
                          <Copy className="w-5 h-5" />
                        </button>}

                        {!readOnly && <button
                          type="button"
                          onClick={() => deleteLabel(e)}
                          disabled={busyId === e.id}
                          className="w-10 h-10 rounded-[12px] bg-red-50 text-red-600 flex items-center justify-center hover:bg-red-600 hover:text-white transition-all disabled:opacity-50"
                          title="Elimina etichetta"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {!readOnly && duplicate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4">
          <form
            onSubmit={submitDuplicate}
            className="w-full max-w-md rounded-[24px] bg-white p-6 shadow-2xl"
          >
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-[#003B71]">Duplica etichetta</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {duplicate.preparazione || duplicate.lotto}. Tutte le altre informazioni resteranno invariate.
                </p>
              </div>
              <button type="button" onClick={() => setDuplicate(null)} className="rounded-full p-2 text-gray-400 hover:bg-gray-100" title="Chiudi">
                <X className="h-5 w-5" />
              </button>
            </div>

            <label className="mb-4 block text-sm font-semibold text-gray-700">
              Data e ora di produzione
              <input
                type="datetime-local"
                required
                value={duplicateDates.data_produzione}
                onChange={(e) => setDuplicateDates((dates) => ({ ...dates, data_produzione: e.target.value }))}
                className="mt-2 w-full rounded-[14px] border border-gray-200 bg-gray-50 px-4 py-3 font-normal focus:outline-none focus:ring-2 focus:ring-[#003B71]"
              />
            </label>

            <label className="mb-6 block text-sm font-semibold text-gray-700">
              Data di scadenza
              <input
                type="date"
                required
                min={duplicateDates.data_produzione.substring(0, 10)}
                value={duplicateDates.scadenza}
                onChange={(e) => setDuplicateDates((dates) => ({ ...dates, scadenza: e.target.value }))}
                className="mt-2 w-full rounded-[14px] border border-gray-200 bg-gray-50 px-4 py-3 font-normal focus:outline-none focus:ring-2 focus:ring-[#003B71]"
              />
            </label>

            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setDuplicate(null)} className="rounded-[14px] border border-gray-200 px-4 py-2.5 text-sm font-bold text-gray-600">
                Annulla
              </button>
              <button type="submit" disabled={busyId === duplicate.id} className="rounded-[14px] bg-[#003B71] px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50">
                {busyId === duplicate.id ? "Duplicazione..." : "Crea duplicato"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
